import { pickedFile } from '../../utils/pickedFile.js'
import MediaCaptureField from '../common/MediaCaptureField.js'
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import Loader from '../common/Loader.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { paymentService } from '../../services/ShipmentPaymentService.js';
import { shipmentService } from '../../services/shipmentService.js';
import Alert from '../common/Alert.js';
import { Plus, Filter, MapPin, ArrowLeft, CreditCard } from "lucide-react";
import { validators, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import PaymentQrModal from '../common/PaymentQrModal.js';


export default function AddPayment() {
  const navigate = useNavigate();
  const { shipmentId } = useParams();
  const { user } = useAuth();
  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    payment_amount: [(v) => validators.required(v, 'Payment amount'), (v) => validators.number(v, 0.01)],
    payment_mode:   [(v) => validators.select(v, 'payment mode')],
  };

  const [loading, setLoading] = useState(false);
  const [fetchingShipment, setFetchingShipment] = useState(true);
  const [shipmentDetails, setShipmentDetails] = useState(null);
  const [formData, setFormData] = useState({
    payment_amount: '',
    payment_mode: 'cash',
    remarks: ''
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [showPaymentQr, setShowPaymentQr] = useState(false);

  const paymentModes = [
    { value: 'cash', label: 'CASH' },
    { value: 'upi', label: 'UPI' },
  ];

  useEffect(() => {
    fetchShipmentDetails();
  }, [shipmentId]);

  const fetchShipmentDetails = async () => {
    try {
      setFetchingShipment(true);
      const response = await shipmentService.getShipment(shipmentId);
      const shipment = response.data.shipment || response.data.data;

      if (user?.role === 'Agent' && String(shipment?.user_id || '') !== String(user?.user_id || '')) {
        Alert.error('You can only add payment on your own shipments');
        navigate(`/shipments/view/${shipmentId}`);
        return;
      }
      if (Number(shipment?.balance_amount || 0) <= 0) {
        Alert.error('No balance due on this shipment');
        navigate(`/shipments/view/${shipmentId}`);
        return;
      }

      setShipmentDetails(shipment);
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to fetch shipment details');
      navigate('/shipments');
    } finally {
      setFetchingShipment(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const rules = validationRules[name];
    const rulesArray = rules ? (Array.isArray(rules) ? rules : [rules]) : null;

    if (name === 'payment_amount') {
      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (rulesArray) validateField(name, value, rulesArray);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (rulesArray) validateField(name, value, rulesArray);
    }
  };

    const handleReceiptChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Receipt must be less than 5MB');
        return;
      }
      setReceiptFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setReceiptPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removeReceipt = () => {
    setReceiptFile(null);
    setReceiptPreview(null);
    const fileInput = document.getElementById('receipt-input');
    if (fileInput) fileInput.value = '';
  };

  const calculateNewBalance = () => {
    const paymentAmount = parseFloat(formData.payment_amount || 0);
    const currentBalance = shipmentDetails?.balance_amount || 0;
    
    // Use toFixed to avoid floating point precision issues
    return parseFloat((currentBalance - paymentAmount).toFixed(2));
  };

  const isPaymentAmountExceeded = () => {
    const paymentAmount = parseFloat(formData.payment_amount || 0);
    const currentBalance = shipmentDetails?.balance_amount || 0;
    return parseFloat(paymentAmount.toFixed(2)) > parseFloat(currentBalance.toFixed(2));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate(formData, validationRules)) return;

    const paymentAmount = parseFloat(formData.payment_amount);

    if (parseFloat(paymentAmount.toFixed(2)) > parseFloat(shipmentDetails.balance_amount.toFixed(2))) {
      Alert.error(`Payment amount cannot exceed balance (₹${shipmentDetails.balance_amount.toFixed(2)})`);
      return;
    }

    const confirmMessage = `Record payment of ₹${paymentAmount.toFixed(2)} for shipment ${shipmentId}?`;

    const confirmed = await Alert.confirm(confirmMessage, 'Confirm Payment');

    if (!confirmed) return;

    try {
      setLoading(true);
      const payload = new FormData();
        Object.keys(formData).forEach(key => {
          if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
            payload.append(key, formData[key]);
          }
        });
        if (receiptFile && !['upi', 'online'].includes(formData.payment_mode)) {
          payload.append('receipt', receiptFile);
        }
        const response = await paymentService.addPayment(shipmentId, payload);
        if (response.data?.requires_online_payment && response.data?.payment_intent) {
          setPaymentIntent(response.data.payment_intent);
          setShowPaymentQr(true);
          setLoading(false);
          return;
        }
      Alert.success('Payment recorded successfully');
      navigate(`/shipments/view/${shipmentId}`);
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleOnlinePaymentSuccess = () => {
    setShowPaymentQr(false);
    setPaymentIntent(null);
    Alert.success('Payment received and recorded successfully');
    navigate(`/shipments/view/${shipmentId}`);
  };

  const handleOnlinePaymentCancel = () => {
    setShowPaymentQr(false);
    setPaymentIntent(null);
    Alert.info('Online payment cancelled. No payment was recorded.');
  };

  if (fetchingShipment) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading shipment details..." />
        </div>
      </DashboardLayout>
    )
  }

  const newBalance = calculateNewBalance();
  const isFullyPaid = shipmentDetails?.balance_amount <= 0;
  const isAmountExceeded = isPaymentAmountExceeded();
  const isSubmitDisabled = loading || isFullyPaid || isAmountExceeded;

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="mb-6">
                  <div className="flex items-center gap-3">
                    <div className='flex w-full'>
                      <h1 className="text-2xl sm:text-xl font-bold text-gray-700 flex items-center gap-2 sm:gap-3">
                        <CreditCard className="w-7 h-7 text-buttonhoverTxt" />Record Payment</h1>
                    </div>
                    <div className="flex justify-end w-full">
                      <div className="flex items-center gap-3 sm:gap-4 md:gap-6">
                        <Button variant="secondary" onClick={() => navigate(`/shipments/view/${shipmentId}`)} icon={ArrowLeft}>
                          Back to Shipment
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shipment Summary Card */}
                {shipmentDetails && (
                  <div className="rounded-xl bg-white p-6 mb-6 overflow-hidden relative">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-orange-100 rounded-full -mr-20 -mt-20 opacity-50"></div>
                    <div className="relative">
                      <div className="w-full">
                        <h2 className="w-full text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                          Shipment Summary
                        </h2>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                        <div className="p-4">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Shipment ID</p>
                          <p className="font-semibold text-gray-700 text-lg">#{shipmentDetails.shipment_id}</p>
                        </div>
                        
                        <div className="p-4">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Total Amount</p>
                          <p className="font-semibold text-gray-700 text-lg">₹{shipmentDetails.total_amount?.toFixed(2)}</p>
                        </div>
                        
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <p className="text-xs font-semibold uppercase tracking-wide">Paid Amount</p>
                          </div>
                          <p className="font-semibold text-gray-700 text-lg">₹{shipmentDetails.paid_amount?.toFixed(2)}</p>
                        </div>
                        
                        <div className={`p-4 ${
                          isFullyPaid 
                            ? '' 
                            : ''
                        }`}>
                          <div className="flex items-center gap-2 mb-1">
                            {isFullyPaid ? (
                              <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                            )}
                            <p className={`text-xs font-medium uppercase tracking-wide ${
                              isFullyPaid ? 'font-semibold text-gray-700' : 'text-red-700'
                            }`}>
                              {isFullyPaid ? 'Fully Paid' : 'Balance Due'}
                            </p>
                          </div>
                          <p className={`font-bold text-lg ${
                            isFullyPaid ? 'font-semibold text-gray-700' : 'text-red-700'
                          }`}>
                            ₹{shipmentDetails.balance_amount?.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      
                      {isFullyPaid && (
                        <div className="mt-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-4">
                          <div className="flex items-center gap-3 text-white">
                            <div className="bg-white/20 p-2 rounded-xl">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div>
                              <p className="font-bold text-sm">Payment Complete!</p>
                              <p className="text-xs text-green-50">This shipment has been fully paid</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Payment Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-6 py-4 border-b">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      Payment Details
                    </h3>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Payment Amount */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Payment Amount
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <span className="text-gray-500 font-semibold">₹</span>
                        </div>
                        <input
                          type="text"
                          name="payment_amount"
                          value={formData.payment_amount}
                          onChange={handleChange}
                          placeholder="0.00"
                          required
                          disabled={isFullyPaid}
                          className={`w-full pl-9 pr-4 py-3 text-base border rounded-xl focus:outline-none transition-all font-semibold disabled:bg-gray-100 disabled:cursor-not-allowed ${
                            (isAmountExceeded && formData.payment_amount) || errors.payment_amount
                              ? 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-transparent'
                              : 'border-gray-300 focus:ring-2 focus:ring-blue-400 focus:border-transparent'
                          }`}
                        />
                      </div>
                      <ValidationMessage error={errors.payment_amount} />
                      <div className="flex items-center justify-between mt-2 text-xs">
                        <span className="text-gray-500">
                          Maximum: <span className="font-semibold text-gray-700">₹{shipmentDetails?.balance_amount?.toFixed(2)}</span>
                        </span>
                        {formData.payment_amount && shipmentDetails && (
                          <>
                            {isAmountExceeded ? (
                              <span className="text-red-600 font-semibold bg-red-50 px-3 py-1 rounded-full flex items-center gap-1">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Amount Exceeds Balance
                              </span>
                            ) : (
                              <span className="text-orange-600 font-semibold bg-orange-50 px-3 py-1 rounded-full">
                                New Balance: ₹{newBalance.toFixed(2)}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Payment Mode */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Payment Mode
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                        </div>
                        <SelectField variant="form"
                          name="payment_mode"
                          value={formData.payment_mode}
                          onChange={handleChange}
                          required
                          disabled={isFullyPaid}
                          className={`w-full pl-12 pr-4 py-3 text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-all appearance-none bg-white font-semibold ${errors.payment_mode ? 'border-red-400' : 'border-gray-300'}`}
                          style={{backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em'}}
                        >
                          {paymentModes.map(mode => (
                            <option key={mode.value} value={mode.value}>{mode.label}</option>
                          ))}
                        </SelectField>
                      </div>
                      <ValidationMessage error={errors.payment_mode} />
                    </div>

                    {/* Remarks */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Remarks
                        <span className="text-gray-400 text-xs ml-2 font-normal">(Optional)</span>
                      </label>
                      <div className="relative">
                        <textarea
                          name="remarks"
                          value={formData.remarks}
                          onChange={handleChange}
                          placeholder="Add any additional notes or comments about this payment..."
                          rows={4}
                          disabled={isFullyPaid}
                          className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
                        />
                        <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                          {formData.remarks.length} characters
                        </div>
                      </div>
                    </div>

                    {/* Receipt Upload */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Receipt
                        <span className="text-gray-400 text-xs ml-2 font-normal">(Optional)</span>
                      </label>
                      <MediaCaptureField mode="document" disabled={isFullyPaid} onPick={handleReceiptChange} />
                      <p className="text-xs text-gray-500 mt-1">Upload receipt image or PDF (Max 5MB)</p>
                      {receiptPreview && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                          <div className="relative inline-block">
                            {receiptFile?.type === 'application/pdf' ? (
                              <div className="w-32 h-32 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                                <div className="text-center">
                                  <svg className="w-10 h-10 mx-auto text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                                  </svg>
                                  <p className="text-xs text-gray-600 mt-1">PDF File</p>
                                </div>
                              </div>
                            ) : (
                              <img src={receiptPreview} alt="Receipt Preview" className="w-32 h-32 object-cover border border-gray-300 rounded-md" />
                            )}
                            <button
                              type="button"
                              onClick={removeReceipt}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 text-sm"
                            >×</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3">
                    <Button variant="primary" type="submit" disabled={isSubmitDisabled} loading={loading}>
                      {loading ? 'Recording Payment...' : 'Record Payment'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate(`/shipments/view/${shipmentId}`)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
      <PaymentQrModal
        open={showPaymentQr}
        intent={paymentIntent}
        title="Scan UPI QR to Record Payment"
        onSuccess={handleOnlinePaymentSuccess}
        onCancel={handleOnlinePaymentCancel}
      />
    </DashboardLayout>
  );
}